import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  Case,
  ClientLens,
  CreateCaseDocument,
  CreateCaseMutationVariables,
  CreateProfileExportConfigDocument,
  DeleteCaseDocument,
  DeleteProfileExportConfigDocument,
  GetCaseByIdDocument,
  GetClientLensDocument,
  GetCompletedReviewsByAssigneeDocument,
  GetCompletedReviewsByAssigneeQuery,
  GetCompletedReviewsTimelineDocument,
  GetCompletedReviewsTimelineQuery,
  GetLensCasesDocument,
  GetLensCasesQueryVariables,
  GetLensChronologyConfigDocument,
  GetLensChronologyConfigQuery,
  GetLensHomeDocument,
  GetLensHomeQuery,
  GetLensOutcomeStatusConfigurationDocument,
  GetLensSbrStatsDocument,
  GetLensSbrStatsQuery,
  GetLookerFoldersDocument,
  GetLookerFoldersQuery,
  GetPendingReviewsForLensDocument,
  GetPendingReviewsForLensQuery,
  GetProfileExportConfigsDocument,
  GetProfileExportConfigsQuery,
  GetReviewCurrentStatsDocument,
  GetReviewCurrentStatsQuery,
  GetReviewerLensSbrStatsDocument,
  GetReviewerLensSbrStatsQuery,
  GetReviewerStatsDocument,
  GetReviewerStatsQuery,
  ProfileExportConfigInput,
  ProfileExportConfigView,
  PutValueGroupTagValuesDocument,
  PutValueGroupTagValuesMutationVariables,
  TimeUnit,
  UpdateProfileExportConfigDocument,
} from "generated/graphql";
import gqlClient, { queryOrFail, runGqlMutation, runGqlQuery } from "gqlClient";
import type { RootState } from "redux/store";
import { extractGQLError } from "shared/PatternGraphQLError";

interface DeleteClientLensCaseProps {
  caseId: number;
}

export interface FetchClientLensProps {
  lensId: number;
}

export const fetchLensOutcomeStatusConfiguration = createAsyncThunk(
  "lens/fetchLensOutcomeStatusConfiguration",
  async (lensId: number) => runGqlQuery(GetLensOutcomeStatusConfigurationDocument, { lensId })
);

export const fetchClientLens = createAsyncThunk<ClientLens, FetchClientLensProps>(
  "lens/fetchClientLens",
  ({ lensId }) => runGqlQuery(GetClientLensDocument, { lensId }).then((r) => r.lens.clientLens)
);

export const fetchLensHome = createAsyncThunk<GetLensHomeQuery, { lensId: number }>("lens/fetchLensHome", (params) =>
  runGqlQuery(GetLensHomeDocument, params)
);

export const fetchClientLensCases = createAsyncThunk<
  {
    cases: Case[];
    total: number;
  },
  GetLensCasesQueryVariables
>("lensHome/fetchCases", async (variables, thunkApi) => {
  try {
    return await runGqlQuery(GetLensCasesDocument, variables).then((response) => response.lens.cases);
  } catch (e: any) {
    return thunkApi.rejectWithValue(extractGQLError(e));
  }
});

export const deleteClientLensCase = createAsyncThunk<{ caseId: number }, DeleteClientLensCaseProps>(
  "lensHome/deleteCase",
  async ({ caseId }) => {
    return await queryOrFail(gqlClient.mutation(DeleteCaseDocument, { caseId }).toPromise()).then(() => ({ caseId }));
  }
);

export const createClientLensCase = createAsyncThunk<Case, CreateCaseMutationVariables>(
  "lensHome/createCase",
  (props, thunkApi) => {
    try {
      return runGqlMutation(CreateCaseDocument, props).then((res) =>
        runGqlQuery(GetCaseByIdDocument, { caseId: res.createCase.id }).then((r) => r.case.data)
      );
    } catch (e: any) {
      return thunkApi.rejectWithValue(extractGQLError(e));
    }
  }
);

export const fetchProfileExportConfigs = createAsyncThunk<GetProfileExportConfigsQuery, { lensId: number }>(
  "lensHome/profileExportConfigs",
  async ({ lensId }) => await runGqlQuery(GetProfileExportConfigsDocument, { lensId })
);

export const createProfileExportConfig = createAsyncThunk<
  ProfileExportConfigView,
  {
    lensId: number;
    name: string;
    exportConfig: ProfileExportConfigInput;
  }
>("lensHome/createProfileExportConfig", async ({ lensId, name, exportConfig }) => {
  const configId = await runGqlMutation(CreateProfileExportConfigDocument, {
    lensId,
    name,
    exportConfig,
  }).then((res) => res.lens.upsertProfileExportConfig);

  return {
    config: exportConfig,
    name,
    id: configId,
    lensId,
    updatedAt: Date.now().toString(),
  };
});

export const updateProfileExportConfig = createAsyncThunk<
  ProfileExportConfigView,
  {
    lensId: number;
    name: string;
    exportConfig: ProfileExportConfigInput;
    configId: number;
  }
>("lensHome/updateProfileExportConfig", async ({ lensId, name, exportConfig, configId }) => {
  await runGqlMutation(UpdateProfileExportConfigDocument, {
    configId,
    lensId,
    name,
    exportConfig,
  });

  return {
    config: exportConfig,
    name,
    lensId,
    id: configId,
    updatedAt: Date.now().toString(),
  };
});

export const deleteProfileExportConfig = createAsyncThunk<number, { configId: number }>(
  "lensHome/deleteProfileExportConfig",
  async ({ configId }) =>
    await runGqlMutation(DeleteProfileExportConfigDocument, { configId }).then((_) => {
      return configId;
    })
);

export const fetchChronologyConfig = createAsyncThunk<GetLensChronologyConfigQuery, { lensId: number }>(
  "lensHome/fetchChronologyConfig",
  async ({ lensId }) => await runGqlQuery(GetLensChronologyConfigDocument, { lensId })
);

// Reports Actions
export const fetchLookerFolderEmbeds = createAsyncThunk<[number, GetLookerFoldersQuery], { lensId: number }>(
  "lens/reports/fetchLookerFolderEmbeds",
  ({ lensId }) =>
    runGqlQuery(GetLookerFoldersDocument, { lensId }).then((resp) => {
      return [lensId, resp];
    })
);

export const ensureLookerEmbeds = createAsyncThunk<[number, GetLookerFoldersQuery], { lensId: number }>(
  "lens/reports/ensureLookerEmbeds",
  ({ lensId }, { dispatch }) => {
    return dispatch(fetchLookerFolderEmbeds({ lensId })).unwrap();
  },
  {
    condition: ({ lensId }, { getState }) => {
      const { clientLens } = getState() as RootState;
      if (clientLens.lookerLensId === lensId && clientLens.lookerReports.embedItems.length > 0) {
        // A false return value signals cancellation of the thunk
        return false;
      }
    },
  }
);
// Lens Admin Actions
interface FetchLensMetricsProps {
  lensId: number;
  firmId?: number;
}

interface FetchLensMetricsByTimeProps {
  lensId: number;
  backPeriod: number;
  unit: TimeUnit;
  firmId?: number;
}

export const fetchPendingReviewsByAssignee = createAsyncThunk<GetPendingReviewsForLensQuery, FetchLensMetricsProps>(
  "lens/AdminFetchPendingReviewsByAssignee",
  async ({ lensId, firmId }) => {
    return await runGqlQuery(GetPendingReviewsForLensDocument, { lensId, firmId });
  }
);

export const fetchCompletedReviewsByAssignee = createAsyncThunk<
  GetCompletedReviewsByAssigneeQuery,
  FetchLensMetricsByTimeProps
>("lens/AdminFetchCompletedReviewsByAssignee", async ({ unit, backPeriod, lensId, firmId }) => {
  return await runGqlQuery(GetCompletedReviewsByAssigneeDocument, { lensId, interval: { unit, backPeriod }, firmId });
});

export const fetchCompletedReviewsTimeline = createAsyncThunk<
  GetCompletedReviewsTimelineQuery,
  FetchLensMetricsByTimeProps
>("lens/AdminFetchCompletedReviewsTimeline", async ({ unit, backPeriod, lensId, firmId }) => {
  return await runGqlQuery(GetCompletedReviewsTimelineDocument, { lensId, interval: { unit, backPeriod }, firmId });
});

// Lens Reviewer Actions
interface FetchAssigneeLensMetricsByTimeProps {
  lensId: number;
  assigneeId: number;
  backPeriod: number;
  unit: TimeUnit;
  firmId?: number;
}

interface FetchLensMetricProps {
  lensId: number;
  assigneeId?: number;
  firmId?: number;
}

export const fetchReviewerStats = createAsyncThunk<GetReviewerStatsQuery, FetchAssigneeLensMetricsByTimeProps>(
  "lens/ReviewerFetchReviewerStats",
  ({ assigneeId, unit, backPeriod, lensId, firmId }) =>
    runGqlQuery(GetReviewerStatsDocument, { lensId, assigneeId, interval: { unit, backPeriod }, firmId })
);

export const fetchCaseStats = createAsyncThunk<GetReviewCurrentStatsQuery, FetchLensMetricProps>(
  "lens/ReviewerFetchCaseStats",
  ({ lensId, assigneeId, firmId }) => runGqlQuery(GetReviewCurrentStatsDocument, { lensId, assigneeId, firmId })
);

interface FetchReviewerSbrStatsProps {
  lensId: number;
  assigneeId: number;
  sectionId?: number;
  firmId?: number;
  unit: TimeUnit;
  backPeriod: number;
}

interface FetchSbrStatsProps {
  lensId: number;
  assigneeId?: number;
  sectionId?: number;
  firmId?: number;
  unit: TimeUnit;
  backPeriod: number;
}

export const fetchSbrReviewerStats = createAsyncThunk<GetReviewerLensSbrStatsQuery, FetchReviewerSbrStatsProps>(
  "lens/fetchSbrReviewerStats",
  ({ lensId, assigneeId, sectionId, firmId, unit, backPeriod }) =>
    runGqlQuery(GetReviewerLensSbrStatsDocument, {
      lensId,
      sectionId,
      assigneeId,
      interval: {
        unit,
        backPeriod,
      },
      firmId,
    })
);

export const fetchSbrCaseStats = createAsyncThunk<GetLensSbrStatsQuery, FetchSbrStatsProps>(
  "lens/fetchSbrCaseStats",
  ({ lensId, sectionId, assigneeId, firmId, unit, backPeriod }) =>
    runGqlQuery(GetLensSbrStatsDocument, {
      lensId,
      sectionId,
      assigneeId,
      interval: {
        unit,
        backPeriod,
      },
      firmId,
    })
);

export const tagValueGroup = createAsyncThunk<
  PutValueGroupTagValuesMutationVariables,
  PutValueGroupTagValuesMutationVariables
>("lens/PutValueGroupTagValues", (args) => runGqlMutation(PutValueGroupTagValuesDocument, args).then(() => args));
